/*
Bollinger Bandwidth Calculator

Bandwidth = (Upper Band - Lower Band) / Middle Band * 100
Or: Bandwidth = 200 * (Standard Deviations * StdDev) / MA * 100

Based on ChartIQ's calculateGenericEnvelope implementation.
*/

import { deviation } from "d3-array";

import { last, slidingWindow, zipper, getSourcePathAccessor } from "../utils";
import getMACalculator from "../utils/getMACalculator";

import { BollingerBandwidth as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, multiplier, movingAverageType, sourcePath } = options;

    const source = getSourcePathAccessor(sourcePath);

    // Use centralized MA calculator for all moving average types
    const maCalculator = getMACalculator(movingAverageType || "sma");
    const meanAlgorithm = maCalculator().options({ windowSize, sourcePath });

    const bandwidthAlgorithm = slidingWindow()
      .windowSize(windowSize)
      .accumulator((values) => {
        const avg = last(values).mean;
        const stdDev = deviation(values, (each) => source(each.datum));
        const totalShift = multiplier * stdDev;

        // Bandwidth formula: 200 * totalShift / middle
        // This represents (upper - lower) / middle * 100
        const bandwidth = avg !== 0 ? (200 * totalShift) / avg : 0;

        return bandwidth;
      });

    const zip = zipper().combine((datum, mean) => ({ datum, mean }));

    const tuples = zip(data, meanAlgorithm(data));
    return bandwidthAlgorithm(tuples);
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    return windowSize - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
